import React, { useState, useEffect, useContext } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import { AuthContext } from '../App'
import axios from 'axios'
import { toast } from 'sonner'
import { AlertTriangle, Mic, Eye, Save, Loader2 } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import AdvancedImageViewer from '../components/AdvancedImageViewer'

const VerifyMedicines = () => {
  const { API } = useContext(AuthContext)
  const location = useLocation()
  const navigate = useNavigate()
  const [medicines, setMedicines] = useState([])
  const [imageBase64, setImageBase64] = useState(null)
  const [drugForms, setDrugForms] = useState([])
  const [uomList, setUomList] = useState([])
  const [setName, setSetName] = useState('')
  const [saving, setSaving] = useState(false)
  const [showImageDialog, setShowImageDialog] = useState(false)
  const [listeningIndex, setListeningIndex] = useState(null)
  const [fileType, setFileType] = useState('image')
  const [isEditing, setIsEditing] = useState(false)
  const [editingSetId, setEditingSetId] = useState(null)
  const [invalidFields, setInvalidFields] = useState([])
  const hasProcessedData = React.useRef(false)

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasProcessedData.current) return
    
    // React Router v7 fix: Try sessionStorage if location.state is empty
    let medicinesData = location.state
    
    if (!medicinesData?.medicines) {
      const storedData = sessionStorage.getItem('ocrNavigationData')
      if (storedData) {
        try {
          medicinesData = JSON.parse(storedData)
        } catch (e) {
          console.error('Failed to parse sessionStorage data:', e)
        }
      }
    }
    
    if (!medicinesData?.medicines) {
      navigate('/upload')
      return
    }
    
    // Mark data as processed and clear sessionStorage
    hasProcessedData.current = true
    sessionStorage.removeItem('ocrNavigationData')

    // Process medicines and detect duplicates
    const processedMedicines = medicinesData.medicines.map(m => ({
      ...m,
      mrp: m.mrp || ''  // Keep actual MRP from OCR
    }))
    
    // Detect duplicates based on name+form+strength
    const seen = {}
    processedMedicines.forEach((med, idx) => {
      const key = `${(med.name || '').trim().toUpperCase()}|${(med.form || '').trim().toUpperCase()}|${(med.strength || '').trim().toUpperCase()}`
      
      if (key in seen) {
        processedMedicines[idx].is_duplicate = true
        processedMedicines[seen[key]].is_duplicate = true
      } else {
        seen[key] = idx
        processedMedicines[idx].is_duplicate = false
      }
    })

    setMedicines(processedMedicines)
    setImageBase64(medicinesData.imageBase64)
    setFileType(medicinesData.fileType || 'image')
    
    // Check if we're editing an existing set
    if (medicinesData.isEditing) {
      setIsEditing(true)
      setEditingSetId(medicinesData.setId)
      setSetName(medicinesData.setName || '')
    }
    
    fetchConfig(processedMedicines)
  }, [])

  const fetchConfig = async (initialMedicines = null) => {
    try {
      const [formsRes, uomRes] = await Promise.all([
        axios.get(`${API}/config/drug-forms`),
        axios.get(`${API}/config/item-uom`)
      ])
      setDrugForms(formsRes.data.forms)
      const uomData = uomRes.data.uom_list
      setUomList(uomData)
      
      // Update qty_calc for all medicines based on their UoM
      // Use initialMedicines if provided, otherwise use current state
      const medsToUpdate = initialMedicines || medicines
      const updatedMedicines = medsToUpdate.map(med => {
        if (med.uom) {
          const uomItem = uomData.find(item => item.uom === med.uom)
          if (uomItem) {
            return { ...med, qty_calc: uomItem.qty_calculation }
          }
        }
        return med
      })
      setMedicines(updatedMedicines)
    } catch (error) {
      console.error('Failed to fetch config:', error)
    }
  }

  const handleMedicineChange = (index, field, value) => {
    const updatedMedicines = [...medicines]
    updatedMedicines[index][field] = value

    // Auto-fill qty_calc based on UoM (from Item_UoM_r1 mapping)
    if (field === 'uom') {
      const uomItem = uomList.find(item => item.uom === value)
      if (uomItem) {
        updatedMedicines[index].qty_calc = uomItem.qty_calculation
      }
    }

    setMedicines(updatedMedicines)
  }

  const startVoiceInput = (index) => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Speech recognition is not supported in your browser')
      return
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    const recognition = new SpeechRecognition()
    recognition.lang = 'en-US'
    recognition.continuous = false
    recognition.interimResults = false

    setListeningIndex(index)

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript
      handleMedicineChange(index, 'name', transcript)
      toast.success('Voice input captured!')
    }

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      toast.error('Voice input failed: ' + event.error)
    }

    recognition.onend = () => {
      setListeningIndex(null)
    }

    recognition.start()
  }

  const isFormValid = () => {
    // Check that all mandatory fields are filled
    const allFieldsFilled = medicines.every(m => m.name && m.form && m.uom)
    // Also check if set name is provided
    return allFieldsFilled && setName.trim().length > 0
  }

  const isFieldInvalid = (index, field) => {
    return invalidFields.some(f => f.index === index && f.field === field)
  }

  const handleSave = async () => {
    if (!setName) {
      toast.error('Please enter a name for this medicine set')
      document.querySelector('[data-testid="set-name-input"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      document.querySelector('[data-testid="set-name-input"]')?.focus()
      return
    }

    // Check for missing mandatory fields
    const missingFields = []
    medicines.forEach((medicine, index) => {
      if (!medicine.name) missingFields.push({ index, field: 'name' })
      if (!medicine.form) missingFields.push({ index, field: 'form' })
      if (!medicine.uom) missingFields.push({ index, field: 'uom' })
      if (!medicine.mrp) missingFields.push({ index, field: 'mrp' })
    })

    if (missingFields.length > 0) {
      setInvalidFields(missingFields)
      toast.error('Fill all mandatory fields', { duration: 5000 })
      
      // Scroll to first missing field and focus
      const firstMissing = missingFields[0]
      const selector = `[data-testid="medicine-${firstMissing.field}-${firstMissing.index}"]`
      const element = document.querySelector(selector)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setTimeout(() => {
          const input = element.querySelector('input, select, button')
          input?.focus()
          input?.click()
        }, 500)
      }
      return
    }

    // Clear invalid fields if validation passes
    setInvalidFields([])
    setSaving(true)

    try {
      if (isEditing && editingSetId) {
        // Update existing medicine set
        await axios.put(`${API}/medicine-sets/${editingSetId}`, {
          set_name: setName,
          medicines: medicines,
          image_base64: imageBase64
        })
        toast.success('Medicine set updated successfully!')
      } else {
        // Create new medicine set
        await axios.post(`${API}/medicine-sets`, {
          set_name: setName,
          medicines: medicines,
          image_base64: imageBase64
        })
        toast.success('Medicine set saved successfully!')
      }
      navigate('/all-sets')
    } catch (error) {
      console.error('Save error:', error)
      toast.error(error.response?.data?.detail || 'Failed to save medicine set')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Layout title="Verify Extracted Data">
      <div className="warning-banner" data-testid="ocr-warning">
        <AlertTriangle className="warning-icon" />
        <div className="warning-content">
          <h4>Important: OCR Accuracy Notice</h4>
          <p>OCR technology may make mistakes in extracting data from images. Please review all fields carefully, verify medication names and dosages, and make necessary corrections before saving this set. Your careful review ensures patient safety.</p>
          <p className="mt-2 font-semibold">Note: Duplicate medicines (same name, form, and strength) are highlighted in pink and will be listed only once in the Excel export.</p>
        </div>
      </div>

      <div className="card">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Medicines Captured</h2>
          <Button variant="outline" onClick={() => setShowImageDialog(true)} data-testid="view-image-button">
            <Eye className="w-4 h-4 mr-2" />
            View Image
          </Button>
        </div>

        <div className="table-container">
          <table data-testid="medicines-table">
            <thead>
              <tr>
                <th style={{minWidth: '250px'}}>Name *</th>
                <th>Form *</th>
                <th>Strength</th>
                <th>Generic Name</th>
                <th>UoM *</th>
                <th>Qty Calc</th>
                <th>Manufacturer</th>
                <th>MRP *</th>
              </tr>
            </thead>
            <tbody>
              {medicines.map((medicine, index) => (
                <tr key={index} className={medicine.is_duplicate ? 'bg-pink-50' : ''}>
                  <td style={{minWidth: '250px', backgroundColor: isFieldInvalid(index, 'name') ? '#fee2e2' : 'transparent'}}>
                    <div className="flex gap-2">
                      <Input
                        value={medicine.name || ''}
                        onChange={(e) => handleMedicineChange(index, 'name', e.target.value)}
                        placeholder="Medicine name"
                        data-testid={`medicine-name-${index}`}
                        className={isFieldInvalid(index, 'name') ? 'border-red-400' : ''}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => startVoiceInput(index)}
                        disabled={listeningIndex === index}
                        data-testid={`voice-input-${index}`}
                        title="Use voice input"
                      >
                        <Mic className={`w-4 h-4 ${listeningIndex === index ? 'text-red-500 animate-pulse' : ''}`} />
                      </Button>
                    </div>
                  </td>
                  <td style={{backgroundColor: isFieldInvalid(index, 'form') ? '#fee2e2' : 'transparent'}}>
                    <Select value={medicine.form || ''} onValueChange={(value) => handleMedicineChange(index, 'form', value)}>
                      <SelectTrigger data-testid={`medicine-form-${index}`} className={isFieldInvalid(index, 'form') ? 'border-red-400' : ''}>
                        <SelectValue placeholder="Form" />
                      </SelectTrigger>
                      <SelectContent>
                        {drugForms.map(form => (
                          <SelectItem key={form} value={form}>{form}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className={!medicine.strength ? 'highlight-cell' : ''}>
                    <Input
                      value={medicine.strength || ''}
                      onChange={(e) => handleMedicineChange(index, 'strength', e.target.value)}
                      placeholder="e.g., 500mg"
                      data-testid={`medicine-strength-${index}`}
                    />
                  </td>
                  <td>
                    <Input
                      value={medicine.generic_name || ''}
                      onChange={(e) => handleMedicineChange(index, 'generic_name', e.target.value)}
                      placeholder="Generic name"
                      data-testid={`medicine-generic-${index}`}
                    />
                  </td>
                  <td style={{backgroundColor: isFieldInvalid(index, 'uom') ? '#fee2e2' : 'transparent'}}>
                    <Select value={medicine.uom || ''} onValueChange={(value) => handleMedicineChange(index, 'uom', value)}>
                      <SelectTrigger data-testid={`medicine-uom-${index}`} className={isFieldInvalid(index, 'uom') ? 'border-red-400' : ''}>
                        <SelectValue placeholder="UoM" />
                      </SelectTrigger>
                      <SelectContent>
                        {uomList.map(item => (
                          <SelectItem key={item.uom} value={item.uom}>{item.uom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <Select value={medicine.qty_calc || ''} onValueChange={(value) => handleMedicineChange(index, 'qty_calc', value)}>
                      <SelectTrigger data-testid={`medicine-qty-calc-${index}`}>
                        <SelectValue placeholder="Qty Calc" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="Qty in Freq*days">Qty in Freq*days</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className={!medicine.manufacturer ? 'highlight-cell' : ''}>
                    <Input
                      value={medicine.manufacturer || ''}
                      onChange={(e) => handleMedicineChange(index, 'manufacturer', e.target.value)}
                      placeholder="Manufacturer"
                      data-testid={`medicine-manufacturer-${index}`}
                    />
                  </td>
                  <td style={{backgroundColor: isFieldInvalid(index, 'mrp') ? '#fee2e2' : 'transparent'}}>
                    <Input
                      value={medicine.mrp || ''}
                      onChange={(e) => handleMedicineChange(index, 'mrp', e.target.value)}
                      placeholder="MRP"
                      type="number"
                      step="0.01"
                      data-testid={`medicine-mrp-${index}`}
                      className={isFieldInvalid(index, 'mrp') ? 'border-red-400' : ''}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Name the Medicine Set</h2>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <Input
              value={setName}
              onChange={(e) => setSetName(e.target.value)}
              placeholder="e.g., Supplier Name - Date"
              data-testid="set-name-input"
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || !isFormValid()}
            data-testid="save-button"
            className="min-w-[150px]"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEditing ? 'Updating...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Update Set' : 'Save Set'}
              </>
            )}
          </Button>
        </div>
      </div>

      {showImageDialog && imageBase64 && (
        <AdvancedImageViewer
          imageBase64={imageBase64}
          onClose={() => setShowImageDialog(false)}
          title="Original Delivery Challan"
        />
      )}
    </Layout>
  )
}

export default VerifyMedicines